import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getPlace, resetPlace } from '../../../store/actions/place';
import { getStoryWithSubtitles, resetStory, resetSubtitles, getSubtitles } from '../../../store/actions/story';
import { updateAppState } from '../../../store/actions/app';
import { RootState, CustomObject } from '../../../types/data';
import StoryWindow from '../story-v2/window';
import MockStoryWindow from '../story-mock/window';
import { useLocation, useHistory, useParams } from 'react-router-dom';

import StoryList from '../../../components/xr/list/story';
import SpotList from '../../../components/xr/list/spot';
import { useMedia, useActions, useSocket } from '../../../hooks';
import { MediaEvents, MediaType } from '../../../common/media';
import { VideoSpaceResourceId } from '../../../common/constants/xr';

import GatewayService from '../../../services/gateway-service';
import * as GatewayEvents from '../../../common/constants/gateway-events';
import LoaderService from '../../../services/loader-service';
import PermissionService from '../../../services/permission-service';

import { POINT_LOADED } from '../../../common/constants/gateway-events';
import Roles from '../../../common/constants/roles';
import qs from 'qs';
import * as SocketEvents from '../../../common/constants/socket-events';
import PreloadService from '../../../services/preload-service';
import { last } from 'lodash';

type TPointSpaceReduxState = {
  story: RootState['story']['current'];
  storySubtitles: RootState['story']['subtitles'];
  point: RootState['point']['current'];
  space: RootState['app']['space'];
  spaceLoaded: RootState['app']['space']['loaded'];
  isContentVisible: RootState['app']['state']['isContentVisible'];
};

const findSpotInList = (list: any[], findBy: string) => {
  return list.find(spot => spot.to_point.url === findBy);
};

const PointSpace = () => {
  const { search, pathname } = useLocation();
  const history = useHistory();
  const { storyId, placeId } = useParams<{ storyId?: string; placeId?: string }>();
  const { socket_room } = qs.parse(search, { ignoreQueryPrefix: true });

  const media = useMedia(VideoSpaceResourceId, MediaType.Video);
  const Socket = useSocket(!!socket_room);
  const [openedStoryUrl, setOpenedStoryUrl] = useState<string | undefined>();

  const boundActions = useActions({
    getPlace,
    resetPlace,
    getStoryWithSubtitles,
    resetStory,
    resetSubtitles,
    getSubtitles,
    updateAppState,
  });

  const { story, storySubtitles, point, space, spaceLoaded, isContentVisible } = useSelector<
    RootState,
    TPointSpaceReduxState
  >(state => ({
    story: state.story.current,
    storySubtitles: state.story.subtitles,
    point: state.point.current,
    space: state.app.space,
    spaceLoaded: state.app.space.loaded,
    isContentVisible: state.app.state.isContentVisible,
  }));

  const storyListRef = useRef<any>();

  const openStory = useCallback(
    ({ url }: CustomObject) => {
      if (PermissionService.hasRoles([Roles.ROLE_PRESENTER]) && Socket) {
        Socket.emit(SocketEvents.ACTION_TOGGLE_STORY, {
          value: url,
        });
      }

      if (url === pathname || Socket) {
        setOpenedStoryUrl(url);
      } else {
        history.push({
          pathname: url,
          search: search,
        });
      }
    },
    [Socket, history, pathname, search]
  );

  const closeStory = useCallback(() => {
    if (PermissionService.hasRoles([Roles.ROLE_PRESENTER]) && Socket) {
      Socket.emit(SocketEvents.ACTION_TOGGLE_STORY, {
        value: null,
      });
    }

    setOpenedStoryUrl(undefined);
    setTimeout(() => storyListRef.current?.closeOpenedGroup(), 10);
  }, [Socket]);

  const navigateToPointHandler = useCallback(
    spot => {
      if (PermissionService.hasRoles([Roles.ROLE_PARTICIPANT])) {
        return;
      }

      media.pause();
      boundActions.updateAppState({
        spotLookAt: {
          x: -spot.vlookat,
          y: -spot.hlookat,
        },
      });

      history.push({
        pathname: spot.to_point.url,
        search: search,
      });
    },
    [boundActions, history, media, search]
  );

  const openStoryHandler = useCallback(
    (st: CustomObject) => {
      if (PermissionService.hasRoles([Roles.ROLE_PARTICIPANT])) {
        return;
      }
      openStory(st);
    },
    [openStory]
  );

  const closeStoryHandler = useCallback(() => {
    if (PermissionService.hasRoles([Roles.ROLE_PARTICIPANT])) {
      return;
    }

    closeStory();
  }, [closeStory]);

  //// Navigation and story toggle synchronization part

  // participant part
  useEffect(() => {
    if (!PermissionService.hasRoles([Roles.ROLE_PARTICIPANT]) || !Socket) {
      return;
    }

    const handleStateUpdate = ({ data: { currentUrl, openedStory } }: any) => {
      if (currentUrl !== pathname) {
        media.pause();

        const spot = findSpotInList(point.data?.spots || [], currentUrl);

        if (spot) {
          boundActions.updateAppState({
            spotLookAt: {
              x: -spot.vlookat,
              y: -spot.hlookat,
            },
          });
        }

        history.push({
          pathname: currentUrl,
          search: search,
        });
      }

      if (openedStory) {
        openStory({ url: openedStory });
      } else {
        closeStory();
      }
    };

    Socket.on(SocketEvents.SYNC_UPDATE_STATE, handleStateUpdate);
    return () => {
      Socket.off(SocketEvents.SYNC_UPDATE_STATE, handleStateUpdate);
    };
  }, [Socket, closeStory, openStory, history, point, boundActions, media, pathname, search]);

  /// for automatically play point videos
  useEffect(() => {
    if (!media) return;

    const handleReady = () => {
      setTimeout(() => {
        media.setLoop(true);

        if (Socket) {
          media.mute();
        }

        if (media.isPaused()) {
          if (!Socket) {
            media.setVolume(50);
          }

          media.play().catch(() => {
            media.mute();
            media.play().catch(() => console.log('Failed to play point video.'));
          });
        }
      }, 10);
    };

    const handleMute = () => {
      GatewayService.emit(GatewayEvents.SYNC_POINT_AUDIO_STATE, {
        value: media.isMuted(),
      });
    };

    const handlePointAudioEvt = (data: any) => (data.value ? media.mute() : media.unmute());

    media.on(MediaEvents.Ready, handleReady);
    media.on(MediaEvents.Change_Volume, handleMute);
    media.on(MediaEvents.Change_Mute, handleMute);
    GatewayService.on(GatewayEvents.ACTION_TOGGLE_POINT_AUDIO, handlePointAudioEvt);

    return () => {
      media?.off(MediaEvents.Ready, handleReady);
      media?.off(MediaEvents.Change_Volume, handleMute);
      media?.off(MediaEvents.Change_Mute, handleMute);
      GatewayService.off(GatewayEvents.ACTION_TOGGLE_POINT_AUDIO, handlePointAudioEvt);
    };
  }, [Socket, media]);

  ///

  useEffect(() => {
    closeStory();

    if ((storyId || placeId) && !Socket) {
      openStory({ url: pathname });
    }
  }, [storyId, placeId, pathname, openStory, closeStory, Socket]);

  /// get story data from API if `openedStoryUrl` variable changes
  useEffect(() => {
    if (!openedStoryUrl) return;

    const pathParams = openedStoryUrl.split('/');
    const id = pathParams[pathParams.length - 1];
    const isPlace = pathParams[pathParams.length - 2] === 'place';

    (async () => {
      let storyId = id;

      const loaderId = LoaderService.show();

      if (isPlace) {
        const {
          payload: {
            data: { response: res },
          },
          __HANDLED_ERROR__,
        }: any = await boundActions.getPlace(id);

        if (__HANDLED_ERROR__) return;

        storyId = res.story.id;
      }

      await boundActions.getStoryWithSubtitles(storyId);
      LoaderService.hide(loaderId);
    })();

    return () => {
      boundActions.resetPlace();
      boundActions.resetStory();
      boundActions.resetSubtitles();
    };
  }, [boundActions, openedStoryUrl]);

  /// when point data loads emit `POINT_LOADED` event
  const pointId = point.data?.id;
  useEffect(() => {
    if (point.loaded && point.data) {
      boundActions.updateAppState({
        lookAt: {
          x: -point.data.vlookat,
          y: -point.data.hlookat,
        },
      });
      setTimeout(() => {
        const pointIds = point.data.spots.map((spot: any) => last<string>(spot.to_point.url.split('/')));
        PreloadService.loadNextPointsData(pointIds);
      }, 5000);
      GatewayService.emit(POINT_LOADED, point.data);
    }
  }, [boundActions, pointId, point]);

  // if (!point.loaded || !point.data) {
  //   return null;
  // }

  const isStoryLoaded = story.loaded && !!story.data && storySubtitles.loaded && spaceLoaded && isContentVisible;

  const { data = {} } = point;

  const { stories, spots } = data;

  let openedStoryData;

  if (!!openedStoryUrl && isStoryLoaded) {
    openedStoryData = story.data;
  }

  return (
    <group visible={spaceLoaded} position={[0, 0, 0]}>
      <StoryList ref={storyListRef} data={stories} activeStory={openedStoryData} onSelect={openStoryHandler} />

      {!PermissionService.hasRoles([Roles.ROLE_PARTICIPANT]) ? (
        <SpotList data={spots} prevSpace={space.history[0]} onSelect={navigateToPointHandler} />
      ) : null}

      {openedStoryData ? (
        openedStoryData.isMockStory || openedStoryData.isMockGame ? (
          <MockStoryWindow data={openedStoryData} subtitles={storySubtitles.data} onClose={closeStoryHandler} />
        ) : (
          <StoryWindow data={openedStoryData} subtitles={storySubtitles.data} onClose={closeStoryHandler} />
        )
      ) : null}
    </group>
  );
};

export default PointSpace;
